/**
 * ResponsibilityCardView - Shared Responsibility Presentation Component
 * 
 * Per Doc 06 (Queues & Query Truth Table) and T-RESP-04:
 * - Renders responsibilities identically across ALL surfaces (MyTasks, loop UI, etc.)
 * - Classification is server-authoritative and rendered exactly as provided
 * - This component MUST NOT compute or change REQUIRED/WAITING/NONE
 * 
 * Rendering behavior by classification:
 * - REQUIRED: Primary CTA + deep link + "what you must do" label
 * - WAITING: "Waiting on X because Y" narrative + no CTA
 * - NONE: Diagnostic explanation only (no disabled buttons)
 */

import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Play, 
  ChevronRight,
  ListOrdered,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Hourglass,
  Info,
  Inbox,
  Handshake,
} from 'lucide-react';
import type { 
  ResponsibilityCard,
  ResponsibilityClassification,
  ResponsibilityScope,
} from '../../../types/prioritization/responsibility';
import { 
  getPrimaryAction, 
  hasValidDeepLink,
  isResponsibilityValid,
  getInvalidReasonMessage,
} from '../../../types/prioritization/responsibility';
import { getResponsibilityNavigationHref } from '../../../lib/responsibilityLinks';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { computeLoopAccess } from '../../../hooks/prioritization/useOrgAwareCatchball';

// ============================================================================
// Constants
// ============================================================================

const SCOPE_ICONS: Record<ResponsibilityScope, typeof ListOrdered> = {
  TASK: ListOrdered,
  SESSION: Play,
  CATCHBALL: ExternalLink,
  CYCLE: Clock,
  PLANNING: Clock,
  INTAKE: Inbox,
  STRATEGY: Clock,
  COMMITMENT: Handshake,
  NOTE: Clock,
};

const SCOPE_COLORS: Record<ResponsibilityScope, { bg: string; text: string }> = {
  TASK: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  SESSION: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  CATCHBALL: { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  CYCLE: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  PLANNING: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  INTAKE: { bg: 'bg-[var(--coral-dim)]', text: 'text-[var(--coral)]' },
  STRATEGY: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  COMMITMENT: { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  NOTE: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
};

const CLASSIFICATION_STYLES: Record<ResponsibilityClassification, { bg: string; border: string; text: string }> = {
  REQUIRED: { bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]' },
  WAITING: { bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]' },
  NONE: { bg: 'bg-[var(--surface)]', border: 'border-[var(--rule)]', text: 'text-[var(--text-sec)]' },
};

// Invalid card styles - used when is_valid === false (per Doc 08: show diagnostics)
const INVALID_STYLES = { bg: 'bg-[var(--coral-dim)]', border: 'border-[var(--coral)]', text: 'text-[var(--coral)]' };

function deriveCatchballWorkLink(deepLink: string | null): string | null {
  if (!deepLink) return null;
  const match = deepLink.match(/\/prioritization\/(?:cascade|catchball)\/(?:inbox|dashboard|compose|workbench)\/([^/?#]+)/);
  if (!match) return null;
  return match[1] ?? null;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDeadline(deadline?: string | null): { text: string; isUrgent: boolean } | null {
  if (!deadline) return null;
  const d = new Date(deadline);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (diff < 0) {
    return { text: 'Overdue', isUrgent: true };
  } else if (days === 0) {
    return { text: 'Due today', isUrgent: true };
  } else if (days === 1) {
    return { text: 'Due tomorrow', isUrgent: false };
  } else if (days < 7) {
    return { text: `Due in ${days} days`, isUrgent: false };
  } else {
    return { text: `Due ${d.toLocaleDateString()}`, isUrgent: false };
  }
}

// ============================================================================
// Component Props
// ============================================================================

export interface ResponsibilityCardViewProps {
  /** The responsibility card from the server */
  card: ResponsibilityCard;
  /** Callback when navigation fails - triggers refresh per T-RESP-07 */
  onNavigationError?: (cardId: string, error: string) => void;
  /** Whether to show a compact version */
  compact?: boolean;
  /** Custom class name for the container */
  className?: string;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * ResponsibilityCardView renders a single responsibility card.
 * 
 * Per T-RESP-04: This component must render identically across all surfaces.
 * The classification is server-authoritative and rendered exactly as provided.
 */
export function ResponsibilityCardView({ 
  card, 
  onNavigationError,
  compact = false,
  className = '',
}: ResponsibilityCardViewProps) {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const userId = useUserStore(state => state.userId);
  const loops = useCatchballLoopStore(state => state.loops);
  const cycles = useDegapProcessStore(state => state.cycles);
  const orgUnits = useEnterpriseIdentityStore(state => state.orgUnits);
  const userNodeIndex = useEnterpriseIdentityStore(state => state.userNodeIndex);
  
  const ScopeIcon = SCOPE_ICONS[card.scope];
  const scopeColors = SCOPE_COLORS[card.scope];
  // Use invalid styles when is_valid is false (per Doc 08)
  const isValid = isResponsibilityValid(card);
  const classificationStyles = isValid 
    ? CLASSIFICATION_STYLES[card.classification]
    : INVALID_STYLES;
  
  const primaryAction = getPrimaryAction(card);
  const deadline = formatDeadline(card.deadline);
  const resolvedDeepLink = useMemo(() => {
    if (card.scope !== 'CATCHBALL' || !card.deep_link || !userId) {
      return card.deep_link;
    }

    const cycleId = deriveCatchballWorkLink(card.deep_link);
    if (!cycleId) return card.deep_link;

    const cycle = cycles.find(entry => entry.id === cycleId);
    const actionableLoop = loops
      .filter(loop => loop.cycleId === cycleId && loop.state !== 'closed')
      .find(loop =>
        computeLoopAccess(loop, userId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId).canActOnLoop
      );

    if (!actionableLoop) return card.deep_link;
    return `/prioritization/cascade/workbench/${cycleId}/${actionableLoop.id}`;
  }, [card.scope, card.deep_link, userId, cycles, loops, orgUnits, userNodeIndex]);

  const navigationTarget = useMemo(
    () => getResponsibilityNavigationHref(card, resolvedDeepLink ?? undefined),
    [card, resolvedDeepLink]
  );
  
  /**
   * Handle navigation with resilience.
   * Per T-RESP-07: If deep link fails at click time → show error + refresh responsibilities
   */
  const handleNavigation = useCallback(async () => {
    if (!navigationTarget) {
      onNavigationError?.(card.id, 'No valid link available');
      return;
    }
    
    setIsNavigating(true);
    
    try {
      navigate(navigationTarget);
    } catch (err) {
      onNavigationError?.(card.id, err instanceof Error ? err.message : 'Navigation failed');
    } finally {
      setIsNavigating(false);
    }
  }, [card.id, navigate, onNavigationError, navigationTarget]);
  
  // ========================================================================
  // Compact Rendering
  // ========================================================================
  
  if (compact) {
    return (
      <div className={`flex items-center justify-between gap-3 p-3  border ${classificationStyles.border} ${classificationStyles.bg} ${className}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Icon - show warning for invalid items per Doc 08 */}
          <div className={`p-1.5  ${
            !isValid ? 'bg-[var(--coral-dim)]' :
            card.classification === 'NONE' ? 'bg-[var(--hover-tint)]' :
            card.classification === 'WAITING' ? 'bg-[var(--mustard-dim)]' : 
            scopeColors.bg
          }`}>
            {!isValid ? (
              <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />
            ) : card.classification === 'NONE' ? (
              <Info className="h-4 w-4 text-[var(--text-ter)]" />
            ) : card.classification === 'WAITING' ? (
              <Hourglass className="h-4 w-4 text-[var(--mustard)]" />
            ) : (
              <ScopeIcon className={`h-4 w-4 ${scopeColors.text}`} />
            )}
          </div>
          
          {/* Subject & Classification */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--text-pri)] truncate">{card.subject}</p>
            <p className="text-xs text-[var(--text-ter)] truncate">
              {!isValid && card.invalid_reason 
                ? getInvalidReasonMessage(card.invalid_reason)
                : card.why
              }
            </p>
          </div>
        </div>
        
        {/* Action - per Doc 08: no disabled buttons, show diagnostics for invalid */}
        {!isValid ? (
          <span className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-[var(--coral)] bg-[var(--coral-dim)]">
            {card.invalid_reason || 'Invalid'}
          </span>
        ) : card.classification === 'REQUIRED' && primaryAction ? (
          <button
            onClick={handleNavigation}
            disabled={isNavigating}
            className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300"
          >
            {isNavigating ? <Loader2 className="h-3 w-3 animate-spin" /> : primaryAction.label}
          </button>
        ) : card.classification === 'WAITING' ? (
          <span className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)]">
            Waiting
          </span>
        ) : null}
      </div>
    );
  }
  
  // ========================================================================
  // Full Rendering
  // ========================================================================
  
  return (
    <div className={`p-4 border-b last:border-b-0 hover:bg-[var(--surface)] transition-colors ${
      !isValid ? 'bg-[var(--coral-dim)]/50 border-l-4 border-l-red-400' :
      card.classification === 'NONE' ? 'opacity-70' : ''
    } ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          {/* Scope Icon - show warning for invalid items per Doc 08 */}
          <div className={`p-2  ${
            !isValid
              ? 'bg-[var(--coral-dim)]'
              : card.classification === 'NONE'
                ? 'bg-[var(--hover-tint)]'
                : card.classification === 'WAITING'
                  ? 'bg-[var(--mustard-dim)]'
                  : scopeColors.bg
          }`}>
            {!isValid ? (
              <AlertTriangle className="h-5 w-5 text-[var(--coral)]" />
            ) : card.classification === 'NONE' ? (
              <AlertTriangle className="h-5 w-5 text-[var(--text-ter)]" />
            ) : card.classification === 'WAITING' ? (
              <Hourglass className="h-5 w-5 text-[var(--mustard)]" />
            ) : (
              <ScopeIcon className={`h-5 w-5 ${scopeColors.text}`} />
            )}
          </div>
          
          {/* Card Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-[var(--text-pri)]">{card.subject}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${scopeColors.bg} ${scopeColors.text}`}>
                {card.scope}
              </span>
              {/* Show INVALID badge when is_valid is false (per Doc 08) */}
              {!isValid ? (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)]">
                  INVALID
                </span>
              ) : (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${classificationStyles.bg} ${classificationStyles.text}`}>
                  {card.classification}
                </span>
              )}
            </div>
            
            {/* Why explanation */}
            <p className="text-sm text-[var(--text-sec)] mt-1">{card.why}</p>
            
            {/* Invalid reason display (per Doc 08: explain why invalid) */}
            {!isValid && card.invalid_reason && (
              <div className="mt-2 p-2 bg-[var(--coral-dim)] border border-[var(--coral)]">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-[var(--coral)] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-[var(--coral)]">
                      {getInvalidReasonMessage(card.invalid_reason)}
                    </span>
                    {card.diagnostics.length > 0 && card.diagnostics[0].remediation && (
                      <p className="text-[var(--coral)] mt-1">
                        {card.diagnostics[0].remediation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Diagnostics (for NONE or blocked items - T-RESP-03 compliant) */}
            {/* Only show if is_valid is true (otherwise we showed the invalid_reason above) */}
            {isValid && card.diagnostics.length > 0 && (
              <div className="mt-2 space-y-1">
                {card.diagnostics.map((diagnostic, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <AlertTriangle className="h-3 w-3 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-[var(--mustard)]">{diagnostic.message}</span>
                      {diagnostic.remediation && (
                        <span className="text-[var(--text-ter)] ml-1">— {diagnostic.remediation}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[var(--text-ter)]">
              {deadline && (
                <span className={`inline-flex items-center gap-1 ${
                  deadline.isUrgent ? 'text-[var(--coral)] font-medium' : ''
                }`}>
                  <Clock className="h-3 w-3" />
                  {deadline.text}
                </span>
              )}
              
              {hasValidDeepLink(card) && (
                <span className="inline-flex items-center gap-1 text-[var(--text-ter)]">
                  <ExternalLink className="h-3 w-3" />
                  {card.deep_link?.split('/').pop()}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Area - Per T-RESP-01/02/03 and Doc 08 (no disabled buttons for invalid) */}
        <div className="flex-shrink-0">
          {/* INVALID: Show diagnostic status, no action buttons (Doc 08) */}
          {!isValid ? (
            <div className="flex flex-col items-end gap-1">
              <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--coral)] bg-[var(--coral-dim)]">
                <AlertTriangle className="h-4 w-4" />
                Not Actionable
              </span>
              <span className="text-xs text-[var(--coral)]">
                {card.invalid_reason?.replace(/_/g, ' ') || 'Invalid task'}
              </span>
            </div>
          ) : card.classification === 'REQUIRED' && primaryAction ? (
            /* REQUIRED: Primary CTA with deep link (T-RESP-01) */
            <button
              onClick={handleNavigation}
              disabled={isNavigating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 transition-colors"
            >
              {isNavigating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {primaryAction.label}
            </button>
          ) : card.classification === 'WAITING' ? (
            /* WAITING: Non-actionable indicator (T-RESP-02) */
            <div className="flex flex-col items-end gap-1">
              <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--mustard)] bg-[var(--mustard-dim)]">
                <Hourglass className="h-4 w-4" />
                Waiting
              </span>
            </div>
          ) : card.classification === 'NONE' ? (
            /* NONE: Diagnostic only, no disabled buttons (T-RESP-03) */
            <div className="flex flex-col items-end gap-1">
              <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--text-ter)] bg-[var(--hover-tint)]">
                <Info className="h-4 w-4" />
                Context Only
              </span>
              {card.diagnostics.length > 0 && (
                <span className="text-xs text-[var(--text-ter)]">{card.diagnostics[0].issue}</span>
              )}
            </div>
          ) : navigationTarget ? (
            <Link
              to={navigationTarget}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              View Details
              <ChevronRight className="h-4 w-4" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ResponsibilityCardView;
